import { ArrowRight } from 'lucide-react';
import { lazy, Suspense } from 'react';
import { Button } from '@/components/ui/button';
import { Tabs } from '@/lib/types/tabs';
import { useNavigation, useWorkspace } from '@/providers';

// Defer the notebook editor (codemirror + xterm terminal, ~hundreds of KB gzip)
// until a workspace is actually connected, keeping those editor libraries out of
// the initial bundle. NotebookPage stays mounted across tabs; only the heavy
// editor subtree is lazy.
const NotebookEditor = lazy(() => import('@/components/Notebook/NotebookEditor'));

export default function NotebookPage() {
  const { connectedWorkspace } = useWorkspace();
  const { switchTab } = useNavigation();

  // Show placeholder when no workspace is connected
  if (!connectedWorkspace) {
    return (
      <div className="flex h-full w-full flex-col items-center justify-center bg-gray-50 dark:bg-gray-900">
        <div className="text-center">
          {/* Icon */}
          <div className="mx-auto mb-5 flex h-14 w-14 items-center justify-center rounded-full bg-gray-100 dark:bg-gray-800">
            <svg
              width="28"
              height="28"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="1.5"
              strokeLinecap="round"
              strokeLinejoin="round"
              className="text-gray-400 dark:text-gray-500"
            >
              <rect x="2" y="3" width="20" height="14" rx="2" ry="2" />
              <line x1="8" y1="21" x2="16" y2="21" />
              <line x1="12" y1="17" x2="12" y2="21" />
            </svg>
          </div>

          {/* Title */}
          <h2 className="mb-2 font-medium text-gray-700 text-lg dark:text-gray-300">No Active Workspace</h2>

          {/* Action */}
          <Button size="sm" onClick={() => switchTab(Tabs.HOME)}>
            Select Workspace
            <ArrowRight className="h-3.5 w-3.5" />
          </Button>
        </div>
      </div>
    );
  }

  return (
    <div className="absolute inset-0">
      <Suspense
        fallback={
          <div className="flex h-full w-full items-center justify-center bg-gray-50 dark:bg-gray-900">
            <div className="h-6 w-6 animate-spin rounded-full border-2 border-gray-300 border-t-gray-600 dark:border-gray-700 dark:border-t-gray-300" />
          </div>
        }
      >
        <NotebookEditor handleCodePaneEvents={false} />
      </Suspense>
    </div>
  );
}
